import { useEffect, useState } from 'react'
import { useParams, useNavigate, Link as RouterLink } from 'react-router-dom'
import { clientApi } from '@/services/api'
import { useClientStore } from '@/stores/clientStore'
import type { Client, ClientViewer } from '@/types'
import { Copy, Check, Link, Trash2, Zap, Lock } from 'lucide-react'
import toast from 'react-hot-toast'
import { Card, CardContent } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Skeleton } from '@/components/ui/skeleton'
import { Separator } from '@/components/ui/separator'

export default function ClientSettings() {
  const { clientId } = useParams<{ clientId: string }>()
  const navigate = useNavigate()
  const setCurrentClient = useClientStore((s) => s.setCurrentClient)
  const [client, setClient] = useState<Client | null>(null)
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [inviteEmail, setInviteEmail] = useState('')
  const [inviting, setInviting] = useState(false)
  const [inviteUrl, setInviteUrl] = useState('')
  const [copied, setCopied] = useState(false)
  const [viewers, setViewers] = useState<ClientViewer[]>([])
  const [removingId, setRemovingId] = useState<string | null>(null)
  const [form, setForm] = useState({
    name: '',
    entity_type: 'company',
    registration_no: '',
    address: '',
    country: '',
    currency: 'USD',
    financial_year_end: 12,
    plan_tier: 'starter',
    lock_date: '',
  })

  useEffect(() => {
    if (!clientId) return
    Promise.all([
      clientApi.get(clientId),
      clientApi.listViewers(clientId),
    ]).then(([clientRes, viewersRes]) => {
      const c: Client = clientRes.data.client
      setClient(c)
      setCurrentClient(c)
      setForm({
        name: c.name,
        entity_type: c.entity_type,
        registration_no: c.registration_no,
        address: c.address,
        country: c.country,
        currency: c.currency,
        financial_year_end: c.financial_year_end,
        plan_tier: c.plan_tier,
        lock_date: c.lock_date || '',
      })
      setViewers(viewersRes.data.viewers || [])
    }).finally(() => setLoading(false))
  }, [clientId])

  async function handleSave(e: React.FormEvent) {
    e.preventDefault()
    if (!clientId) return
    setSaving(true)
    try {
      const res = await clientApi.update(clientId, form)
      const updated = res.data.client
      setClient(updated)
      setCurrentClient(updated)
      toast.success('Client settings saved')
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed to save'
      toast.error(msg)
    } finally {
      setSaving(false)
    }
  }

  async function handleInviteViewer(e: React.FormEvent) {
    e.preventDefault()
    if (!clientId || !inviteEmail) return
    setInviting(true)
    setInviteUrl('')
    try {
      const res = await clientApi.inviteViewer(clientId, inviteEmail)
      const { token } = res.data
      const url = `${window.location.origin}/invite/${token}`
      setInviteUrl(url)
      setInviteEmail('')
      toast.success(`Invite sent to ${inviteEmail}`)
    } catch (err: unknown) {
      const msg = (err as { response?: { data?: { detail?: string } } })?.response?.data?.detail || 'Failed to create invite'
      toast.error(msg)
    } finally {
      setInviting(false)
    }
  }

  function handleCopy() {
    navigator.clipboard.writeText(inviteUrl)
    setCopied(true)
    setTimeout(() => setCopied(false), 2000)
  }

  async function handleRemoveViewer(userId: string) {
    if (!clientId) return
    setRemovingId(userId)
    try {
      await clientApi.removeViewer(clientId, userId)
      setViewers((v) => v.filter((x) => x.user_id !== userId))
      toast.success('Viewer access removed')
    } catch {
      toast.error('Failed to remove viewer')
    } finally {
      setRemovingId(null)
    }
  }

  if (loading) {
    return (
      <div className="max-w-2xl space-y-5">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-96 w-full" />
      </div>
    )
  }

  return (
    <div className="max-w-2xl space-y-5">
      <div>
        <h1 className="text-[17px] font-bold text-[#000A1E] leading-[24px]">Client Settings</h1>
        <p className="text-[13px] text-[rgba(0,10,30,0.75)] mt-0.5">{client?.name}</p>
      </div>

      {/* Main Settings Form */}
      <Card>
        <CardContent className="p-6">
          <form onSubmit={handleSave} className="space-y-5">
            <h2 className="text-[17px] font-bold text-[#000A1E] leading-[24px] pb-3 border-b border-border">Details</h2>

            <div className="space-y-1.5">
              <Label>Client Name</Label>
              <Input
                type="text"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Entity Type</Label>
                <select
                  value={form.entity_type}
                  onChange={(e) => setForm({ ...form, entity_type: e.target.value })}
                  className="input"
                >
                  <option value="company">Company</option>
                  <option value="sole_trader">Sole Trader</option>
                  <option value="trust">Trust</option>
                  <option value="partnership">Partnership</option>
                  <option value="nonprofit">Non-Profit</option>
                  <option value="other">Other</option>
                </select>
              </div>
              <div className="space-y-1.5">
                <Label>Registration No.</Label>
                <Input
                  type="text"
                  value={form.registration_no}
                  onChange={(e) => setForm({ ...form, registration_no: e.target.value })}
                  placeholder="Optional"
                />
              </div>
            </div>

            <div className="space-y-1.5">
              <Label>Address</Label>
              <Input
                type="text"
                value={form.address}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
                placeholder="Optional"
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Country</Label>
                <Input
                  type="text"
                  value={form.country}
                  onChange={(e) => setForm({ ...form, country: e.target.value })}
                  placeholder="e.g. US"
                />
              </div>
              <div className="space-y-1.5">
                <Label>Currency</Label>
                <select
                  value={form.currency}
                  onChange={(e) => setForm({ ...form, currency: e.target.value })}
                  className="input"
                >
                  <option value="USD">USD</option>
                  <option value="CAD">CAD</option>
                  <option value="GBP">GBP</option>
                  <option value="EUR">EUR</option>
                  <option value="AUD">AUD</option>
                  <option value="LKR">LKR</option>
                  <option value="INR">INR</option>
                  <option value="SGD">SGD</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-1.5">
                <Label>Financial Year End</Label>
                <select
                  value={form.financial_year_end}
                  onChange={(e) => setForm({ ...form, financial_year_end: parseInt(e.target.value) })}
                  className="input"
                >
                  {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                    <option key={m} value={m}>
                      {new Date(2000, m - 1, 1).toLocaleString('default', { month: 'long' })}
                    </option>
                  ))}
                </select>
              </div>
              <div className="space-y-1.5">
                <Label>Plan</Label>
                <select
                  value={form.plan_tier}
                  onChange={(e) => setForm({ ...form, plan_tier: e.target.value })}
                  className="input"
                >
                  <option value="starter">Starter — $5/mo</option>
                  <option value="premium">Premium — $10/mo</option>
                </select>
              </div>
            </div>

            {/* Lock Date */}
            <Separator />
            <div className="flex items-center gap-3 pt-1">
              <Lock className="w-4 h-4 text-[rgba(0,10,30,0.75)]" />
              <h2 className="text-[17px] font-bold text-[#000A1E] leading-[24px]">Lock Date</h2>
            </div>
            <p className="text-[13px] text-[rgba(0,10,30,0.75)] -mt-3">
              Transactions on or before this date cannot be created, edited, or deleted.
              Clear the date to remove the lock.
            </p>
            <div className="space-y-1.5">
              <Label>Lock accounts through</Label>
              <div className="flex items-center gap-3">
                <Input
                  type="date"
                  value={form.lock_date}
                  onChange={(e) => setForm({ ...form, lock_date: e.target.value })}
                  className="w-48"
                />
                {form.lock_date && (
                  <Button type="button" variant="ghost" size="sm" onClick={() => setForm({ ...form, lock_date: '' })}>
                    Clear
                  </Button>
                )}
              </div>
            </div>

            {/* Upgrade prompt for Starter clients */}
            {form.plan_tier === 'starter' && (
              <div className="p-3.5 bg-[#0078C8]/5 border border-[#0078C8]/10 rounded-xl flex items-start gap-3">
                <Zap className="w-4 h-4 text-[#0078C8] flex-shrink-0 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <p className="text-[13px] font-bold text-[#0078C8] mb-0.5">Unlock Premium features for this client</p>
                  <p className="text-[13px] text-[#0078C8]/80">
                    Budgeting, Fixed Assets, and Tracking Categories are available on Premium ($10/client/month).
                    Change the plan above and save to enable them.{' '}
                    <RouterLink to="/settings/billing" className="underline hover:text-[#0078C8] font-bold">
                      View billing
                    </RouterLink>
                  </p>
                </div>
              </div>
            )}

            <Separator />
            <div className="flex gap-3">
              <Button type="submit" disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </Button>
              <Button type="button" variant="outline" onClick={() => navigate(`/clients/${clientId}/accounts`)}>
                Cancel
              </Button>
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Client Viewers */}
      <Card>
        <CardContent className="p-6 space-y-4">
          <div className="pb-4 border-b border-border">
            <h2 className="text-[17px] font-bold text-[#000A1E] leading-[24px]">Client Viewers</h2>
            <p className="text-[13px] text-[rgba(0,10,30,0.75)] mt-0.5">People with read-only access to this client's reports</p>
          </div>

          {viewers.length === 0 ? (
            <p className="text-[15px] text-[rgba(0,10,30,0.75)] text-center py-4">No viewers added yet</p>
          ) : (
            <div className="divide-y divide-border">
              {viewers.map((v) => (
                <div key={v.user_id} className="flex items-center justify-between py-3">
                  <div>
                    <p className="text-[15px] font-bold text-[#000A1E]">{v.full_name}</p>
                    <p className="text-[13px] text-[rgba(0,10,30,0.75)]">{v.email}</p>
                  </div>
                  <Button
                    variant="ghost" size="icon-sm"
                    onClick={() => handleRemoveViewer(v.user_id)}
                    disabled={removingId === v.user_id}
                    className="text-red-400 hover:text-red-600 hover:bg-red-50"
                    title="Remove viewer"
                  >
                    <Trash2 className="w-3.5 h-3.5" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Invite Viewer */}
      <Card>
        <CardContent className="p-6 space-y-4">
          <div className="pb-4 border-b border-border">
            <h2 className="text-[17px] font-bold text-[#000A1E] leading-[24px]">Invite Client Viewer</h2>
            <p className="text-[13px] text-[rgba(0,10,30,0.75)] mt-0.5">Generate an invite link for the client — valid for 7 days</p>
          </div>
          <form onSubmit={handleInviteViewer} className="flex gap-3">
            <Input
              type="email"
              value={inviteEmail}
              onChange={(e) => setInviteEmail(e.target.value)}
              className="flex-1"
              placeholder="client@example.com"
              required
            />
            <Button type="submit" disabled={inviting}>
              <Link className="w-4 h-4" />
              {inviting ? 'Creating...' : 'Generate Link'}
            </Button>
          </form>

          {inviteUrl && (
            <div className="p-3.5 bg-[#0078C8]/5 rounded-lg border border-[#0078C8]/10">
              <p className="text-[13px] font-bold text-[#0078C8] mb-2">Invite link ready — share this with the client:</p>
              <div className="flex items-center gap-2">
                <code className="text-[13px] text-[#000A1E] bg-white rounded px-2 py-1.5 flex-1 overflow-hidden text-ellipsis whitespace-nowrap border border-border">
                  {inviteUrl}
                </code>
                <Button
                  type="button"
                  size="sm"
                  onClick={handleCopy}
                  className={copied ? 'bg-emerald-500 hover:bg-emerald-500 text-white' : ''}
                >
                  {copied ? <Check className="w-3.5 h-3.5" /> : <Copy className="w-3.5 h-3.5" />}
                  {copied ? 'Copied!' : 'Copy'}
                </Button>
              </div>
              <p className="text-[13px] text-[rgba(0,10,30,0.75)] mt-2">This link expires in 7 days. Generating a new link will invalidate this one.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
